import { Button, ButtonsGroup, Heading, InputSelect, InputSelectChange, Link, PageHeader, Typo } from "#/atoms";
import { CustomTable, FilterAction } from "#/molecules";
import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { FONT_SIZE, NATURAL, PRIMARY_BLUE } from "@/theme";
import { FilterCustomer, FindCustomerModal } from "@/components/organisms";
import { useFilter, useModal } from "@/hooks";

import { ButtonState } from "@/components/atoms/Button/Button.interface";
import { Column } from "react-table";
import { GetListParams } from "@/api/customer/interface";
import { InvoicesProps } from "./Invoices.interface";
import { PageWrapper } from "#/atoms";
import { formatShortDateString } from "@/helpers";
import { useApi } from "@/api";

const Invoices: FC<InvoicesProps> = ({ invoices, isAdmin, loading, numberOfFilters, onFindForCustomer, onMarkAsPaid, onOpenFilter }) => {
  
  const { customer } = useApi();

  const {
    filter,
    numberOfFilters: numberOfCustomerFilters,
    setShouldFilter,
    shouldFilter,
    ...filterProps
  } = useFilter<FilterCustomer>();
  
  const [invoicesToMark, setInvoicesToMark] = useState<string[]>([]);

  const { showModal: showFindCustomerModal, onCloseModal: onCloseCustomerModal, onOpenModal: onOpenCustomerModal } = useModal();
  const [currentPage, setcurrentPage] = useState(1);

  const [loadingCustomers, setLoadingCustomers] = useState(false);
  
  const getCustomers = async (data?: GetListParams) => {
    setLoadingCustomers(true);
    await customer.getList(data);
    setLoadingCustomers(false);
  }
  
  useEffect(() => {
    if (shouldFilter) {
      if (filter) {
        getCustomers({
          accountName: filter.login,
          address: filter.location,
          code: filter.customerCode,
          email: filter.customerEmail,
          isActive: filter.active,
          name: filter.companyName,
          page: currentPage
        });
      } else {
        getCustomers();
      }
      setShouldFilter(false);
    }
  }, [filter, shouldFilter]);

  const handleInputChange: InputSelectChange = useCallback((isChecked, value) => {
    if (isChecked) {
      setInvoicesToMark([...invoicesToMark, value])
    } else {
      setInvoicesToMark(invoicesToMark.filter((item) => item !== value));
    }
  }, [invoicesToMark]);

  const handleSelectAll = () => {
    if (invoicesToMark.length === invoices.length) {
      setInvoicesToMark([]);
    } else {
      setInvoicesToMark(invoices.map((invoice) => invoice.id));
    }
  }
  
  const handleMarkAsPaid = useCallback(() => {
    if (invoicesToMark.length == 0) {
      return;
    }
    
    onMarkAsPaid(invoicesToMark)
  }, []);

  const handleSelectCustomer = useCallback((customerId: string) => {
    const selected = customer.customers.find((item) => item.id === customerId);
    
    if (selected) {
      onFindForCustomer(selected.id);
    }
  }, [customer.customers]);
  
  const data = useMemo(() => (invoices.map((invoice) => ({
    amount: invoice.amount,
    companyName: invoice.companyName,
    externalUrl: invoice.externalUrl,
    finishDate: invoice.finishDate,
    id: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
  }))), [invoices]);

  const getChecked = (val: string) => {
    return invoicesToMark.includes(val);
  }
  
  const columns: Column[] = useMemo(() => [
    {
      Cell: (props) => (
        <InputSelect
          type="checkbox"
          checked={getChecked(props.value)}
          name="invoices"
          value={props.value}
          onChangeValue={handleInputChange}
        />
      ),
      Header: '',
      accessor: 'id',
    },
    {
      Header: '',
      accessor: 'invoiceNumber',
    },
    {
      Cell: ({ value }) => (
        <p> { formatShortDateString(value) }</p>
      ),
      Header: '',
      accessor: 'finishDate',
    },
    {
      Header: 'Company Name',
      accessor: 'companyName',
    },
    {
      Header: 'Billed Amount',
      accessor: 'amount',
    },
    {
      Cell: ({ data: rawData, row, value }) => {
        if (!rawData[row.index].externalUrl) {
          return null;
        }

        return (
          <Link
            href={value}
            download
            fontColor={PRIMARY_BLUE.DARK}
            target="_blank"
          >
            <img src="invoice.png" style={{ height: "40px", width: "40px" }} alt="download" ></img>
          </Link>
        );
      },
      Header: '',
      accessor: 'externalUrl',
      id: 'button'
    },
  ], [invoicesToMark]);

  const buttonState: ButtonState = invoicesToMark.length > 0 ? 'default' : 'disabled';

  return (
    <>
      <PageWrapper>
        <PageHeader>
          <Heading scale="h1" fontColor={NATURAL.K}>Invoices</Heading>
          <Typo fontColor={NATURAL.G} fontSize={FONT_SIZE.SM}>Payments are not updated automatically and may not reflect actual payment status.</Typo>
          <ButtonsGroup>
          <Button onClick={handleSelectAll}>Select All</Button>
          <Button state={buttonState} onClick={handleMarkAsPaid}>Mark as Paid</Button>
          { isAdmin && <Button onClick={onOpenCustomerModal}>Find for Customer</Button> }
          </ButtonsGroup>
        </PageHeader>
        <FilterAction numberOfFilters={numberOfFilters} onOpen={onOpenFilter} />
        <CustomTable columns={columns} data={data} loading={loading} />
      </PageWrapper>
      {showFindCustomerModal && (
          <FindCustomerModal
            customers={customer.customers}
            loading={loadingCustomers}
            onClose={onCloseCustomerModal}
            onSelectCustomer={handleSelectCustomer}
            {...filterProps}
            filter={filter}
            numberOfFilters={numberOfFilters}
          />
        )}
    </>
  );
};

export default memo(Invoices);
